import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ItemManagementService } from "@app/cem/project/services/item-management.service";
import { LookupService } from "@app/cem/project/services/lookup.service";
import { StdCfgSharedServiceService } from "@app/cem/project/standard-config/std-cfg-shared-service.service";
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from "@app/shared/aggrid/pxd-ag-grid-options";
import { IFunctionDto } from '@app/shared/models/dtos/function';
import { IItemDto } from "@app/shared/models/dtos/item";
import { IItemCategoryDto } from '@app/shared/models/dtos/item-category';
import { IItemSubCategoryDto } from '@app/shared/models/dtos/item-sub-category';
import { IProjectSubTypeDto } from "@app/shared/models/dtos/project-subtype";
import { ItemScopingCategory } from '@app/shared/models/item-scoping-category';
import { AgGridAngular } from "ag-grid-angular";
import { Column, GridApi } from 'ag-grid-community';
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-stg-cfg-items',
  templateUrl: './stg-cfg-items.component.html',
  styleUrls: ['./stg-cfg-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
  ],
})
export class StgCfgItemsComponent implements OnInit {
  private stdCfgSharedServiceService = inject(StdCfgSharedServiceService);
  private lookupService = inject(LookupService);
  private itemManagementService = inject(ItemManagementService);
  private errorService = inject(ErrorService);
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  public iItemDtos: Array<IItemDto> = new Array<IItemDto>();
  public IProjectSubTypeDtos: Array<IProjectSubTypeDto> = new Array<IProjectSubTypeDto>();
  private selectedFunction: IFunctionDto;
  private unsubscribe$: Subject<boolean> = new Subject();
  private gridApi: GridApi;
  public loading: boolean = false;
  public dialogOpened = false;
  public IItemCategoryDtos: Array<IItemCategoryDto> = new Array<IItemCategoryDto>();
  public IItemSubCategoryDtos: Array<IItemSubCategoryDto> = new Array<IItemSubCategoryDto>();
  public enabled = true;

  rowClassRules = {
    "red-row": 'data.color == "Red"',
    "green-row": 'data.color == "Green"',
    "blue-row": 'data.color == "Blue"'
  }

  private BASE_URL = this.environment.apiUrl;
  private ItemScopingCategoryList: Array<ItemScopingCategory> = new Array<ItemScopingCategory>();
  private ItemScopingCategoryMap: {};

  async ngOnInit() {
    try {
      this.IItemCategoryDtos = (await this.itemManagementService.findItemTypeDtos()).Results;
      this.IItemSubCategoryDtos = (await this.itemManagementService.findItemSubTypeDtos()).Results;
      await this.initSelectedFunction();
      await this.loadItemScopingCategories();
    }
    catch (error) {
      this.errorService.error("Error loading Items", error)
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    cellSelection: true,
    rowGroupPanelShow: 'never',
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    columnDefs: [
      {
        field: 'ItemNumber', headerName: 'Item Number', editable: false, width: 133, rowDrag: true,
      }
      , { field: 'Name', headerName: 'Description', editable: false, width: 218 }
      , {
        field: 'ItemCategoryId', headerName: 'Category', editable: false,
        valueGetter: params => {
          return this.getIItemCategoryDtosById(params.data.ItemCategoryId);
        },
        width: 111
      }
      , {
        field: 'ItemSubCategoryId', headerName: 'SubCat', editable: false,
        valueGetter: params => {
          return this.getIItemSubCatDtosById(params.data.ItemSubCategoryId);
        },
        width: 79
      }
      , {
        field: 'ItemScopingCategoryId', headerName: 'Scoping Category', editable: false,
        valueGetter: params => {
          return this.ItemScopingCategoryMap[params.data.ItemScopingCategoryId];
        },
        hide: true,
        width: 140
      }
    ],
  };

  toggleScopingCategoryColumn(selectedFunction: IFunctionDto) {
    var column: Column = this.gridApi.getAllGridColumns().find(x => x.getColDef().field == 'ItemScopingCategoryId');
    if (selectedFunction != undefined && selectedFunction.FunctionId == 1) {
      this.gridApi.setColumnsVisible([column], true);
    } else {
      this.gridApi.setColumnsVisible([column], false);
    }
  }

  getRowNodeId(data) {
    return data.ItemId;
  }

  async initSelectedFunction() {
    this.stdCfgSharedServiceService.getSelectedFunctionDto().pipe(takeUntil(this.unsubscribe$)).subscribe(response => {
      this.selectedFunction = response;
      this.loadGrid();
    }
    );
    this.stdCfgSharedServiceService.getSelectedStandardConfigDto().pipe(takeUntil(this.unsubscribe$)).subscribe(response => {
      this.enabled = response && response.StandardConfigurationId > 0;
    })
    let projectsubTypeData = await this.lookupService.findProjectSubTypeDtos();
    this.IProjectSubTypeDtos = projectsubTypeData.Results;

    this.stdCfgSharedServiceService.isItemsLoaded().pipe(takeUntil(this.unsubscribe$)).subscribe(response => {
      this.loadGrid();
    });
  }

  async loadGrid() {
    this.loading = true;
    if (this.selectedFunction !== null && this.selectedFunction !== undefined) {
      this.iItemDtos = this.stdCfgSharedServiceService.IItemDtos.filter(s => s.FunctionId === this.selectedFunction.FunctionId && s.IsActive);
      this.gridApi.setGridOption('rowData', this.iItemDtos);
    } else {
      this.gridApi.setGridOption('rowData', this.iItemDtos);
    }

    this.stdCfgSharedServiceService.setItemGridApi(this.gridApi);

    this.loading = false;
  }

  defaultExcelExportParams = {
    processCellCallback: (params) => {
      if (params.column.userProvidedColDef && params.column.userProvidedColDef.type
        && params.column.userProvidedColDef && params.column.userProvidedColDef.type.length > 0
        && params.column.userProvidedColDef.type.includes('dateColumn')
        && params.value) {
        return params.value ? moment(params.value).format('L') : ''
      }
      return params.value;
    },
  };

  getIProjectSubTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IProjectSubTypeDtos.find(a => a.ProjectSubTypeId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIItemCategoryDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemCategoryDtos.find(a => a.ItemCategoryId == id);
    if (atype) return atype.ItemCategoryName;
    return '';
  }

  getIItemSubCatDtosById = (id) => {
    if (!id) return '';
    let atype = this.IItemSubCategoryDtos.find(a => a.ItemSubCategoryId == id);
    if (atype) return atype.ItemSubCategoryName;
    return '';
  }

  async onGridReady(params) {
    this.gridApi = params.api;
    await this.loadGrid();
  }

  async loadItemScopingCategories() {
    let url = `${this.BASE_URL}ItemScopingCategories?$select=ItemScopingCategoryId,Description`;
    let data: ItemScopingCategory[] = (await this.http.get<any>(url).toPromise()).value;
    this.ItemScopingCategoryList = data;
    this.ItemScopingCategoryMap = new Map();
    this.ItemScopingCategoryList.map(x => this.ItemScopingCategoryMap[x.ItemScopingCategoryId] = x.Description);
  }
}